'use client';

import { c as _c } from "react/compiler-runtime";
import * as _ from 'es-toolkit';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import clsx from 'clsx';
import { useState, useEffect } from 'react';
import { useSession, signOut, authClient } from '@/lib/auth-client';
import { useSignInModal } from '@/lib/ui';
import { useLockBodyScroll } from '@/hooks/use-lock-body-scroll';
import { useKeyboardShortcut } from '@/lib/contexts/KeyboardShortcutsContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { UserIcon, LogOutIcon, GridIcon, Menu, X, Shield, Search } from 'lucide-react';
import { SearchDialog } from '@/components/search/SearchDialog';
import { ImageLoader } from '@/components/ImageLoader';
import { MessagesButton } from '@/components/messaging/MessagesButton';
import { isApprovedTradePartner } from '@/lib/utils/trade';
import { useEarlyPreviewBanner } from '@/components/EarlyPreviewProvider';
interface HeaderProps {
  navItems?: {
    href: string;
    label: string;
  }[];
  searchEnabled?: boolean;
  signInEnabled?: boolean;
}
const DefaultNavItems = [{
  href: '/demand',
  label: 'Demand Central'
},
// { href: '/export', label: 'Export' },
{
  href: '/opportunities',
  label: 'Opportunities'
}, {
  href: '/directory',
  label: 'Directory'
},
// { href: '/whatson', label: "What's On" },
{
  href: '/hubs',
  label: 'Hubs'
}, {
  href: '/tools',
  label: 'Tools'
}
// { href: '/spark', label: 'Spark' },
// { href: '/content?type=news', label: 'News' },
// { href: '/fun', label: 'Fun' },
// { href: '/asks', label: 'Asks' },
// { href: '/host-opportunities', label: 'Host' },
// process.env.SIZZLE_PLATFORM == 'sizzlenz' ? { href: '/nz-trade-hub', label: 'Trade Hub' } : null,
];
const MobileMenuSections = [{
  title: '',
  key: 'top',
  links: [{
    href: '/demand',
    label: 'Demand Central'
  },
  // { href: '/export', label: 'Export Central' },
  {
    href: '/directory',
    label: 'Directory'
  }, {
    href: '/opportunities',
    label: 'Opportunities'
  }, {
    href: '/content?type=news',
    label: 'News'
  },
  // { href: '/whatson', label: "What's On" },
  {
    href: '/spark',
    label: 'Spark'
  },
  // { href: '/fun', label: 'Fun' },
  // { href: '/consumer/discover-do', label: 'Discover & Do' },
  // { href: '/themes', label: 'Themes' },
  // { href: '/asks', label: 'Asks' },
  // { href: '/host-opportunities', label: 'Host' },
  {
    href: '/hubs',
    label: 'Hubs'
  }, {
    href: '/tools',
    label: 'Tools'
  }
  // { href: '/collections', label: 'Collections' },
  ]
}];
export default function Header(props) {
  const $ = _c(75);
  const pathname = usePathname();
  const router = useRouter();
  const {
    data: session,
    isPending
  } = useSession();
  const {
    showSignInModal
  } = useSignInModal();
  const navItems = props.navItems || DefaultNavItems;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const isBannerVisible = useEarlyPreviewBanner();
  const isImpersonating = !!session?.session?.impersonatedBy;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      key: "/",
      metaKey: typeof navigator !== "undefined" && navigator.platform.includes("Mac"),
      ctrlKey: typeof navigator !== "undefined" && !navigator.platform.includes("Mac")
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.searchEnabled) {
    t1 = () => {
      if (props.searchEnabled) {
        setIsSearchDialogOpen(true);
      }
    };
    $[1] = props.searchEnabled;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useKeyboardShortcut("global-search", t0, t1, props.searchEnabled ?? false);
  useLockBodyScroll(isMobileMenuOpen);
  let t2;
  if ($[3] !== router) {
    t2 = async () => {
      await signOut();
      router.push("/");
      setIsMobileMenuOpen(false);
    };
    $[3] = router;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleSignOut = t2;
  let t3;
  if ($[5] !== router) {
    t3 = async () => {
      ;
      try {
        await authClient.admin.stopImpersonating();
        router.push("/admin/users");
        window.location.reload();
      } catch (t4) {
        const error = t4;
        console.error("Failed to stop impersonating:", error);
      }
    };
    $[5] = router;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleStopImpersonating = t3;
  let t4;
  if ($[7] !== showSignInModal) {
    t4 = () => {
      showSignInModal({
        onSuccess: _temp
      });
      setIsMobileMenuOpen(false);
    };
    $[7] = showSignInModal;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleSignInClick = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsMobileMenuOpen(false);
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const closeMobileMenu = t5;
  let t6;
  let t7;
  if ($[10] !== isMobileMenuOpen) {
    t6 = () => {
      const handleEscape = e => {
        if (e.key === "Escape" && isMobileMenuOpen) {
          closeMobileMenu();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    };
    t7 = [isMobileMenuOpen];
    $[10] = isMobileMenuOpen;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  const t8 = isBannerVisible ? "top-[36px]" : "top-0";
  let t9;
  if ($[13] !== t8) {
    t9 = clsx("fixed left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100 transition-[top] duration-200", t8);
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== handleStopImpersonating || $[16] !== isImpersonating || $[17] !== session?.user?.email || $[18] !== session?.user?.name) {
    t10 = isImpersonating && <div className="bg-amber-500 text-white text-center py-1.5 text-sm font-medium">⚠️ You are impersonating {session?.user?.name || session?.user?.email}<button onClick={handleStopImpersonating} className="ml-3 underline hover:no-underline cursor-pointer">Stop Impersonating</button></div>;
    $[15] = handleStopImpersonating;
    $[16] = isImpersonating;
    $[17] = session?.user?.email;
    $[18] = session?.user?.name;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center"><svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" /></svg></div>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Link href="/" className="flex items-center gap-1 flex-shrink-0" onClick={closeMobileMenu}>{t11}<div className="flex flex-col"><span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent leading-tight">Sizzle</span><span className="text-[9px] font-semibold uppercase tracking-wider text-primary-600 leading-none">Early preview</span></div></Link>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== navItems || $[23] !== pathname || $[24] !== session) {
    let t14;
    if ($[26] !== session) {
      t14 = item => {
        if (!item) {
          return false;
        }
        if ("requiresTradePartner" in item && item.requiresTradePartner) {
          return session?.user && isApprovedTradePartner((session.user as any)?.featureFlags);
        }
        return true;
      };
      $[26] = session;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    if ($[28] !== pathname) {
      t15 = item_0 => <Link key={item_0.href} href={item_0.href} className={clsx("px-2 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer", pathname === item_0.href ? "bg-primary-50 text-primary-600" : "text-gray-600 hover:text-gray-900 hover:bg-gray-50")}>{item_0.label}</Link>;
      $[28] = pathname;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    t13 = navItems.filter(t14).map(t15);
    $[22] = navItems;
    $[23] = pathname;
    $[24] = session;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <nav className="hidden md:flex items-center gap-3">{t13}</nav>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== props.searchEnabled) {
    t15 = props.searchEnabled && <button onClick={() => setIsSearchDialogOpen(true)} className="p-1.5 sm:p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer flex-shrink-0" aria-label="Search"><Search className="w-5 h-5" /></button>;
    $[32] = props.searchEnabled;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const t16 = isMobileMenuOpen && "hidden md:flex";
  let t17;
  if ($[34] !== t16) {
    t17 = clsx("flex items-center flex-shrink-0", t16);
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== handleSignInClick || $[37] !== handleSignOut || $[38] !== handleStopImpersonating || $[39] !== isImpersonating || $[40] !== isPending || $[41] !== session) {
    t18 = isPending ? <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" /> : session ? <DropdownMenu><DropdownMenuTrigger asChild={true}><Button variant="ghost" size="sm" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3"><div className="relative"><div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden flex-shrink-0">{session.user.image ? <ImageLoader src={session.user.image} alt={session.user.name || "User"} width={32} height={32} className="w-full h-full rounded-full" /> : <UserIcon className="w-4 h-4 text-primary-600" />}</div>{!(session.user as any)?.approved && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-amber-500 border-2 border-white rounded-full" />}</div></Button></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-56">{isImpersonating && <><div className="px-2 py-1.5 text-xs font-semibold text-amber-600 bg-amber-50 border-b border-amber-100">⚠️ Impersonating User</div></>}<DropdownMenuItem disabled={true} className="flex items-center justify-between"><div className="flex flex-col"><div className="font-medium">{session.user.name || session.user.email}</div>{session.user.name && <div className="text-xs text-gray-500">{session.user.email}</div>}<div className="text-xs text-gray-600 mt-1">{(session.user as any)?.pointsBalance ?? 0} points</div>{!(session.user as any)?.approved && <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-bold border border-amber-100 uppercase tracking-wider mt-1">Pending Approval</span>}</div></DropdownMenuItem><DropdownMenuItem asChild={true}><Link href="/me" className="flex items-center justify-between cursor-pointer w-full"><span className="flex items-center"><UserIcon className="mr-2 h-4 w-4" />My Dashboard</span></Link></DropdownMenuItem><DropdownMenuItem asChild={true}><Link href="/me/collections" className="flex items-center justify-between cursor-pointer w-full"><span className="flex items-center"><GridIcon className="mr-2 h-4 w-4" />My Collections</span></Link></DropdownMenuItem>{(session.user as any)?.isAdmin && <><DropdownMenuSeparator /><DropdownMenuItem asChild={true}><Link href="/admin" className="flex items-center justify-between cursor-pointer w-full"><span className="flex items-center"><Shield className="mr-2 h-4 w-4" />Admin Dashboard</span></Link></DropdownMenuItem></>}{isImpersonating && <><DropdownMenuSeparator /><DropdownMenuItem onClick={handleStopImpersonating} className="cursor-pointer text-amber-600 font-medium"><UserIcon className="mr-2 h-4 w-4" />Stop Impersonating</DropdownMenuItem></>}<DropdownMenuSeparator /><DropdownMenuItem onClick={handleSignOut} className="cursor-pointer"><LogOutIcon className="mr-2 h-4 w-4" />Sign Out</DropdownMenuItem></DropdownMenuContent></DropdownMenu> : <Button size="sm" className="shadow-sm hover:shadow px-3 sm:px-4" onClick={handleSignInClick}>Sign In</Button>;
    $[36] = handleSignInClick;
    $[37] = handleSignOut;
    $[38] = handleStopImpersonating;
    $[39] = isImpersonating;
    $[40] = isPending;
    $[41] = session;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t17 || $[44] !== t18) {
    t19 = <div className={t17}>{t18}</div>;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== isMobileMenuOpen) {
    t20 = () => setIsMobileMenuOpen(!isMobileMenuOpen);
    $[46] = isMobileMenuOpen;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== isMobileMenuOpen) {
    t21 = isMobileMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />;
    $[48] = isMobileMenuOpen;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t20 || $[51] !== t21) {
    t22 = <button onClick={t20} className="p-1.5 sm:p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all flex-shrink-0 cursor-pointer" aria-label="Toggle menu">{t21}</button>;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t15 || $[54] !== t19 || $[55] !== t22) {
    t23 = <div className="flex items-center gap-2 sm:gap-2 flex-shrink-0">{t15}{t19}{t22}</div>;
    $[53] = t15;
    $[54] = t19;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t14 || $[58] !== t23) {
    t24 = <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8"><div className="flex justify-between items-center h-16 min-w-0">{t12}{t14}{t23}</div></div>;
    $[57] = t14;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t10 || $[61] !== t24 || $[62] !== t9) {
    t25 = <header className={t9}>{t10}{t24}</header>;
    $[60] = t10;
    $[61] = t24;
    $[62] = t9;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== isMobileMenuOpen || $[65] !== pathname || $[66] !== session) {
    t26 = isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-white flex flex-col animate-in slide-in-from-top-2 duration-300"><div className="flex-shrink-0 border-b border-gray-100 px-4 py-4"><div className="flex justify-between items-center"><Link href="/" className="flex items-center gap-2" onClick={closeMobileMenu}><div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center"><svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" /></svg></div><div className="flex flex-col"><span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent leading-tight">Sizzle</span><span className="text-[9px] font-semibold uppercase tracking-wider text-primary-600 leading-none">Early preview</span></div></Link><button onClick={closeMobileMenu} className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all" aria-label="Close menu"><X className="w-6 h-6" /></button></div></div><div className="flex-1 overflow-y-auto px-4 py-6"><div className="space-y-8">{MobileMenuSections.map(section => <div key={section.key}>{section.title && <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-4 text-right">{section.title}</h3>}<div className="space-y">{section.links.filter(link => {
                if ("requiresTradePartner" in link && link.requiresTradePartner) {
                  return session?.user && isApprovedTradePartner((session.user as any)?.featureFlags);
                }
                return true;
              }).map(link_0 => <Link key={link_0.href} href={link_0.href} className={clsx("block px-3 py-1 rounded-lg text-lg font-medium transition-all text-right", pathname === link_0.href ? "bg-primary-50 text-primary-600" : "text-gray-700 hover:text-gray-900 hover:bg-gray-50")} onClick={closeMobileMenu}>{link_0.label}</Link>)}</div></div>)}</div><div className="mt-12 pt-8 border-t border-gray-100"><div className="flex flex-col gap-4 items-end"><div className="flex items-center gap-6"><a href="mailto:hello@sizzle.nz" className="flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a1.75 1.75 0 01-1.644 0L1.5 8.67z" /><path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a.25.25 0 00.235 0L22.5 6.908z" /></svg>Contact</a><a href="https://linkedin.com/company/sizzlenz" target="_blank" rel="noopener" className="flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.25 6.5 1.75 1.75 0 016.5 8.25zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.54c1.39 0 3.4.81 3.4 4V19z" /></svg>LinkedIn</a></div><p className="text-sm text-gray-500">© {new Date().getFullYear()} Sizzle CPG. All rights reserved.</p></div></div></div></div>;
    $[64] = isMobileMenuOpen;
    $[65] = pathname;
    $[66] = session;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== isSearchDialogOpen || $[69] !== props.searchEnabled) {
    t27 = props.searchEnabled && <SearchDialog open={isSearchDialogOpen} onOpenChange={setIsSearchDialogOpen} />;
    $[68] = isSearchDialogOpen;
    $[69] = props.searchEnabled;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t25 || $[72] !== t26 || $[73] !== t27) {
    t28 = <>{t25}{t26}{t27}</>;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  return t28;
}
function _temp() {}